<template>
  <div class="d-flex flex-column">
    <div
      class="d-flex align-center justify-space-between"
      style="min-height: 36px"
    >
      <table-name
        :table-number="tableNumber"
        :table-prefix="tablePrefix"
        :table-suffix="tableSuffix"
        class="text-xs mb-1"
      />

      <div
        v-if="addedCustomerTags.length > 0"
        class="d-flex align-center"
      >
        <v-icon
          left
          small
        >
          {{ icons.mdiPound }}
        </v-icon>

        <div style="max-width: 240px">
          <v-chip-group class="customer-tags-cap-slide">
            <v-chip
              v-for="(customerTag) in addedCustomerTags"
              :key="`${tablePrefix}${tableNumber}${tableSuffix}-customerTag${customerTag.id}`"
              small
            >
              {{ customerTag.attributes.name }}
            </v-chip>
          </v-chip-group>
        </div>
      </div>
    </div>

    <v-card
      class="elevation-4 position-relative"
      min-height="236.5"
      :ripple="false"
      :style="getBorderStyle()"
      @click="$emit('clicked')"
    >
      <table-timer
        :start-at="startAt"
        :expire-at="expireAt"
        :finish-notice-minutes-date="finishNoticeMinutesDate"
        :finish-notice-color="finishNoticeColor"
        class="py-4 px-3"
        @finish-notice-colors="onFinishNoticeColors"
      />
      <v-divider></v-divider>

      <v-card-text
        class="d-flex flex-grow-1 flex-column justify-space-between"
      >
        <div class="d-flex align-center justify-space-between">
          <div>
            <v-badge
              :content="customerCount"
              inline
            >
              <v-icon
                large
              >
                {{ icons.mdiAccountOutline }}
              </v-icon>
            </v-badge>
          </div>

          <div class="d-flex">
            <sup>
              <v-icon>
                {{ icons.mdiCurrencyJpy }}
              </v-icon>
            </sup>
            <span
              class="text-2xl font-weight-semibold"
            >
              {{ total.toLocaleString() }}
            </span>
          </div>
        </div>

        <div
          v-if="customerNames"
          class="d-flex align-center customer-names-row"
        >
          <v-icon
            left
            small
          >
            {{ icons.mdiAccountCircleOutline }}
          </v-icon>
          <span class="customer-names text-caption text--secondary">
            {{ customerNames }}
          </span>
        </div>

        <div
          class="d-flex align-center"
          style="min-height: 36px"
        >
          <v-icon
            left
            small
          >
            {{ icons.mdiVectorLink }}
          </v-icon>
          <v-chip-group
            v-if="referringHistories.length > 0"
            column
          >
            <span
              v-for="referringHistory in referringHistories"
              :key="`referringHistory${referringHistory.id}`"
            >
              {{ referringHistory.attributes.user.data.attributes.name }}
              <v-chip small>
                {{ referringHistory.attributes.referral.data.attributes.name }}
              </v-chip>
            </span>
          </v-chip-group>

          <span v-else>---</span>
        </div>

        <nomination-display
          :nomination-histories="currentNominations"
        />
      </v-card-text>

      <v-menu
        top
        left
      >
        <template #activator="{ on: menuOn, attrs: menuAttrs }">
          <v-btn
            icon
            absolute
            style="bottom: 0; right: 0;"
            v-bind="menuAttrs"
            v-on="menuOn"
          >
            <v-icon
              small
              v-text="icons.mdiDotsVertical"
            />
          </v-btn>
        </template>

        <template #default="menu">
          <v-list
            dense
            outlined
          >
            <v-subheader>操作</v-subheader>
            <v-list-item class="mb-4">
              <v-list-item-content>
                <v-list-item-title class="d-flex align-center">
                  <span class="mr-4">移動</span>
                  <div @click.stop>
                    <v-select
                      v-model="moveTableParams.tableNumber"
                      :items="tablesToMove"
                      :prepend-icon="icons.mdiArrowRightBold"
                      dense
                      hide-details
                      @change="onMoveTableSelect($event, menu)"
                    />
                  </div>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-dialog
              v-model="closeDialog"
              max-width="400px"
            >
              <template #activator="{ on: listOn, attrs: listAttrs }">
                <v-list-item
                  v-bind="listAttrs"
                  v-on="listOn"
                >
                  <v-list-item-title class="error--text">
                    卓を削除
                  </v-list-item-title>
                </v-list-item>
              </template>
              <template #default="dialog">
                <v-card>
                  <v-spacer />
                  <v-toolbar
                    flat
                    color="transparent"
                  >
                    削除の確認
                  </v-toolbar>
                  <v-card-text>
                    削除を実行してもよろしいですか（この卓に紐づけたすべての履歴も削除します。）
                  </v-card-text>
                  <v-card-actions class="justify-end">
                    <v-btn
                      text
                      @click="() => {
                        dialog.value = false;
                        menu.value = false;
                      }"
                    >
                      キャンセル
                    </v-btn>
                    <v-btn
                      color="error"
                      @click="deleteTable"
                    >
                      同意の上で削除
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </template>
            </v-dialog>
          </v-list>
        </template>
      </v-menu>
    </v-card>
  </div>
</template>

<script>
import {
  reactive,
  computed,
  inject,
  ref,
  watch,
  onUnmounted,
} from '@vue/composition-api'
import { map } from 'lodash'
import {
  mdiAccountOutline,
  mdiAccountCircleOutline,
  mdiCurrencyJpy,
  mdiVectorLink,
  mdiPound,
  mdiDotsVertical,
  mdiArrowRightBold,
} from '@mdi/js'
import TableName from '@/views/components/table/TableName.vue'
import TableTimer from '@/views/components/table/TableTimer.vue'
import NominationDisplay from '@/views/components/table/NominationDisplay.vue'

export default {
  components: {
    TableName,
    TableTimer,
    NominationDisplay,
  },
  props: {
    id: {
      type: [Number, String],
      required: true,
    },
    hallId: {
      type: [Number, String],
      required: true,
    },
    tablePrefix: {
      type: String,
      default: '',
    },
    tableSuffix: {
      type: String,
      default: '卓',
    },
    tableNumber: {
      type: [String, Number],
      required: true,
    },
    startAt: {
      type: String,
      required: true,
    },
    expireAt: {
      type: String,
      required: false,
      default: null,
    },
    customerCount: {
      type: [Number, String],
      required: true,
    },
    subtotal: {
      type: [Number, String],
      required: true,
    },
    total: {
      type: [Number, String],
      required: true,
    },
    currentNominations: {
      // NOTE: 中身はNominationだけのvending_history
      type: Array,
      default: () => [],
    },
    referringHistories: {
      type: Array,
      default: () => [],
    },
    customerTableTaggings: {
      type: Array,
      default: () => [],
    },
    customers: {
      type: Array,
      default: () => [],
    },
    finishNoticeMinutesDate: {
      type: Object,
      default: () => ({
        first: null,
        second: null,
      }),
    },
    finishNoticeColor: {
      type: String,
      default: null,
    },
    colorCodes: {
      type: Object,
      default: () => ({
        red: '#D74A4A',
        yellow: '#F4C645',
      }),
    },
  },
  setup(props, { emit }) {
    const halls = inject('halls', [])
    const closeDialog = ref(false)
    const currentTime = ref(Date.now())

    const moveTableParams = reactive({
      tableNumber: null,
      hallId: null,
    })

    const addedCustomerTags = computed(() => {
      if (props.customerTableTaggings.length < 1) return []

      return map(props.customerTableTaggings, 'attributes.customerTag.data')
    })

    const customerNames = computed(() => {
      if (!props.customers || props.customers.length === 0) return ''

      return props.customers.map(c => c.name).join('/')
    })

    const tableName = computed(() => {
      return [props.tablePrefix, props.tableNumber, props.tableSuffix].join('')
    })

    const sortedHalls = computed(() => {
      return halls.value.slice().sort((a, b) => {
        return a.attributes.position - b.attributes.position
      })
    })

    const tablesToMove = computed(() => {
      return sortedHalls.value.flatMap(hall => {
        // 現在進行中のテーブル番号を取得
        const ongoingTableNumbers = hall.attributes.tables.data.map(table => table.attributes.tableNumber)

        // テーブルリスト生成
        const tables = Array.from({ length: hall.attributes.tableCount }, (_, i) => {
          const tableNumber = i + 1

          // 現在進行中のテーブルを除外
          if (ongoingTableNumbers.includes(tableNumber)) return null

          return {
            text: [hall.attributes.tablePrefix, tableNumber, hall.attributes.tableSuffix].join(''),
            value: `${hall.id}-${tableNumber}`,
          }
        }).filter(Boolean) // null を除外

        return [{ header: hall.attributes.name }, ...tables]
      })
    })

    const onMoveTableSelect = (value, menu) => {
      // eslint-disable-next-line no-param-reassign
      const callback = () => (menu.value = false)

      const [hallId, tableNumber] = value.split('-')
      moveTableParams.hallId = Number(hallId)
      moveTableParams.tableNumber = Number(tableNumber)

      emit('move-table', { hallId, tableNumber, callback })
    }

    const deleteTable = () => {
      emit('destroy')
    }

    const onFinishNoticeColors = color => {
      const hallId = sortedHalls.value.find(hall => hall.id === props.hallId)?.id
      emit('finish-notice-colors', { hallId: +hallId, tableId: +props.id, color })
    }

    const getBorderStyle = () => {
      // finishNoticeColorが存在する場合はそれを優先
      if (props.finishNoticeColor) {
        if (props.finishNoticeColor === 'red') {
          return `border: 3px solid ${props.colorCodes.red}; background-color: ${props.colorCodes.red}20;`
        }

        if (props.finishNoticeColor === 'yellow') {
          return `border: 3px solid ${props.colorCodes.yellow}; background-color: ${props.colorCodes.yellow}20;`
        }
      }

      // finishNoticeColorがnullの場合のみ、現在の時間に基づいて色を計算
      if (props.expireAt && props.finishNoticeMinutesDate.first && props.finishNoticeMinutesDate.second) {
        const endTime = Date.parse(props.expireAt) - currentTime.value
        const firstLimit = props.finishNoticeMinutesDate.first * 60 * 1000
        const secondLimit = props.finishNoticeMinutesDate.second * 60 * 1000

        if (endTime < secondLimit) {
          return `border: 3px solid ${props.colorCodes.red}; background-color: ${props.colorCodes.red}20;`
        }

        if (endTime < firstLimit) {
          return `border: 3px solid ${props.colorCodes.yellow}; background-color: ${props.colorCodes.yellow}20;`
        }
      }

      return ''
    }

    // 1秒ごとに時間を更新
    const timeInterval = setInterval(() => {
      currentTime.value = Date.now()
    }, 1000)

    // コンポーネントのアンマウント時にインターバルをクリア
    onUnmounted(() => {
      clearInterval(timeInterval)
    })

    return {
      moveTableParams,
      halls,
      closeDialog,

      // computed
      addedCustomerTags,
      customerNames,
      tableName,
      tablesToMove,
      getBorderStyle,

      // methods
      onMoveTableSelect,
      deleteTable,
      onFinishNoticeColors,

      icons: {
        mdiAccountOutline,
        mdiAccountCircleOutline,
        mdiCurrencyJpy,
        mdiVectorLink,
        mdiPound,
        mdiDotsVertical,
        mdiArrowRightBold,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.customer-tags-cap-slide {
  .v-slide-group__prev--disabled, .v-slide-group__next--disabled {
    display: none;
  }
}

.customer-names-row {
  min-height: 24px;
}

.customer-names {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
}
</style>
